import { ERRORS_MESSAGE, REGEX } from "@lhd/constants";
import { useCallback, useState } from "react";
import { RULE_TYPE, TYPE_INPUT } from "../../types";
import type { ValidationError } from "../../types";
import { ColumnDef } from "@lhd/components/Table/hook";
import { convertCharterFullWidth } from "@lhd/utility";

type DataRef<T = any> = {
  current: Record<string, Record<string, T>>;
};

type ReceivedProps = {
  colDef: ColumnDef;
  dataChangedRef?: DataRef;
  dataValidationRef?: DataRef<ValidationError>;
  rowId: string;
  colId: string;
  originalValue: any;
  handleForceRender: () => void;
};

const useValidation = ({
  colDef,
  dataChangedRef,
  dataValidationRef,
  rowId,
  colId,
  originalValue,
  handleForceRender,
}: ReceivedProps) => {
  const { editInfo } = colDef;
  const [isError, setIsError] = useState(
    !!dataValidationRef?.current?.[rowId]?.[colId],
  );
  const handleValidation = useCallback(() => {
    if (!dataValidationRef) return;

    const valueRaw = dataChangedRef?.current?.[rowId]?.[colId];
    const value = String(valueRaw ?? "");
    let validationError: ValidationError | undefined;
    if (
      value &&
      editInfo?.type === TYPE_INPUT.EMAIL &&
      !REGEX.EMAIL.test(value.toLowerCase())
    ) {
      validationError = {
        msg: ERRORS_MESSAGE.EMAIL_INVALID,
        type: RULE_TYPE.INVALID_EMAIL,
      };
    }

    if (
      value &&
      editInfo?.type === TYPE_INPUT.ZIPCODE &&
      !REGEX.ZIP_CODE.test(value.toLowerCase())
    ) {
      validationError = {
        msg: ERRORS_MESSAGE.ZIPCODE,
        type: RULE_TYPE.ZIPCODE,
      };
    }

    const phoneNumber = convertCharterFullWidth(value);
    if (
      value &&
      editInfo?.type === TYPE_INPUT.PHONE_NUMBER &&
      !/^\d{10}$/.test(phoneNumber)
    ) {
      validationError = {
        msg: ERRORS_MESSAGE.PHONE_NUMBER_INVALID,
        type: RULE_TYPE.INVALID_PHONE_NUMBER,
      };
    }

    const registerCode = convertCharterFullWidth(value);
    if (
      value &&
      editInfo?.type === TYPE_INPUT.REGISTER_CODE &&
      !/^[0-9-]+$/.test(registerCode)
    ) {
      validationError = {
        msg: ERRORS_MESSAGE.REGISTER_CODE_INVALID,
        type: RULE_TYPE.INVALID_REGISTER_CODE,
      };
    }

    if (!validationError) {
      for (const { type, msg } of editInfo?.rules ?? []) {
        if (type === RULE_TYPE.REQUIRED && !value.trim()) {
          validationError = {
            msg: msg || ERRORS_MESSAGE.REQUIRED,
            type,
          };
        }
        if (
          type === RULE_TYPE.CAN_NOT_BE_UPDATED_EMPTY &&
          originalValue &&
          !value.trim()
        ) {
          validationError = {
            msg: msg || ERRORS_MESSAGE.CAN_NOT_BE_UPDATED_EMPTY,
            type,
          };
          break;
        }
      }
    }

    if (validationError) {
      if (!isError) {
        setIsError(true);
        handleForceRender();
      }
      if (!dataValidationRef.current[rowId]) {
        dataValidationRef.current[rowId] = {};
      }
      dataValidationRef.current[rowId][colId] = validationError;
      return;
    }

    if (isError) {
      setIsError(false);
      handleForceRender();
    }

    const rowValidation = dataValidationRef.current[rowId];
    if (!rowValidation) return;

    delete rowValidation[colId];
    if (Object.keys(rowValidation).length === 0) {
      delete dataValidationRef.current[rowId];
    }
  }, [
    isError,
    colId,
    dataChangedRef,
    dataValidationRef,
    editInfo?.rules,
    editInfo?.type,
    rowId,
    originalValue,
  ]);

  return { handleValidation, isError };
};

export type Props = ReturnType<typeof useValidation>;

export default useValidation;
